import { AxiosError } from 'axios';
import { useAtom } from 'jotai';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toRelativeUrl } from '@okta/okta-auth-js';
import { useOktaAuth } from '@okta/okta-react';
import { useQuery } from '@tanstack/react-query';

import { getCurrentOpalUser } from '@frontend/api/services/OpaluserService';
import { OpalAuth, opalAuthAtom, opalUserAtom } from '@frontend/atoms';
import { OPAL_BACKEND_URL, RUNTIME_ENV, WS_GATEWAY_URL } from '@frontend/envConfig';
import { useInstanceId } from '@frontend/hooks';
import { getOrRenewOptiIdToken } from '@frontend/oktaAuth';
import { sendSentryError, sendSentryMessage } from '@frontend/utils/sendSentryError';

import AccessDenied from './AccessDenied';
import AtomsHydrator from './AtomsHydrator';
import FullScreenLoader from './FullScreenLoader';

// @ts-expect-error - External module import
const OpalAuthPromise = import('OpalChat/OpalAuth')
  .then(m => m.default as OpalAuth)
  .catch(() => null);

const RequireAuth = ({ children }: { children: JSX.Element }): JSX.Element => {
  const { authState, oktaAuth } = useOktaAuth();
  const { instanceId } = useInstanceId();
  const [opalAuth, setOpalAuth] = useAtom(opalAuthAtom);
  const navigate = useNavigate();

  useEffect(() => {
    const handleError = (error: Error) => {
      sendSentryMessage('TokenManager error', { error: error.message }, 'error');
    };

    oktaAuth.tokenManager.on('error', handleError);

    return () => {
      oktaAuth.tokenManager.off('error');
    };
  }, [oktaAuth]);

  const {
    data: user,
    isLoading: userLoading,
    isError: hasUserFetchError,
    error: userFetchError,
  } = useQuery({
    queryKey: ['current-user', instanceId],
    queryFn: async () => {
      return getCurrentOpalUser(instanceId);
    },
    enabled: Boolean(authState?.isAuthenticated),
  });

  useEffect(() => {
    const login = async () => {
      const originalUri = toRelativeUrl(window.location.href, window.location.origin);
      oktaAuth.setOriginalUri(originalUri);
      await oktaAuth.signInWithRedirect();
    };

    if (!authState) {
      return;
    }
    if (!authState.isAuthenticated) {
      login().catch(sendSentryError);
    }
  }, [authState, oktaAuth, authState?.isAuthenticated]);

  useEffect(() => {
    if (RUNTIME_ENV !== 'production') {
      localStorage.setItem('ai-chat:BASE_URL', OPAL_BACKEND_URL || '');
      localStorage.setItem('ai-chat:SOCKET_URL', WS_GATEWAY_URL || '');
    }
  }, []);

  useEffect(() => {
    if (!user) {
      return;
    }
    const initializeOpalAuth = async () => {
      const OpalAuthModule = await OpalAuthPromise;
      if (OpalAuthModule && user.instance?.instance_id) {
        const accessToken = await getOrRenewOptiIdToken();
        const opalAuth = OpalAuthModule.create(
          user.instance.instance_id,
          accessToken ?? '',
          getOrRenewOptiIdToken
        );
        await opalAuth.initAuth();
        setOpalAuth(opalAuth);
      }
    };
    initializeOpalAuth().catch(sendSentryError);
  }, [oktaAuth, setOpalAuth, user]);

  useEffect(() => {
    if (
      hasUserFetchError &&
      userFetchError instanceof AxiosError &&
      userFetchError.response?.status === 403
    ) {
      navigate('/instances');
    }
  }, [hasUserFetchError, userFetchError, navigate]);

  if (hasUserFetchError) {
    if (userFetchError instanceof AxiosError && userFetchError.response?.status === 403) {
      return <FullScreenLoader />;
    }
    return (
      <AccessDenied
        errorMessage={
          userFetchError instanceof AxiosError
            ? userFetchError.response?.data?.detail
            : userFetchError?.message
        }
        clear
        unauthorized={
          userFetchError instanceof AxiosError && userFetchError.response?.status === 401
        }
      />
    );
  }

  if (!authState || !authState.isAuthenticated || userLoading || !user || !opalAuth) {
    return <FullScreenLoader />;
  }

  return <AtomsHydrator initialValues={[[opalUserAtom, user]]}>{children}</AtomsHydrator>;
};

export default RequireAuth;
